import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../lib/components/custom/Icon";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { SearchInput } from "../../lib/components/ui/search-input";
import { Skeleton } from "../../lib/components/ui/skeleton";
import { useApi } from "../../lib/hooks/api/use-api";
import Layout from "../layout";
import OrderCard from "./OrderCard";

export default function Orders() {
	const { t } = useTranslation();
	const [activeSearchQuery, setActiveSearchQuery] = useState("");

	const { auth, orders } = useApi();
	const { data: session } = auth.useSession();
	const {
		data: ordersData,
		isLoading,
		error,
		refetch,
	} = orders.getByUserId({ userId: session?.user?.id || "" });

	// Search orders when there's an active search query
	const { data: searchData, isLoading: isSearching } = orders.searchByItem({
		q: activeSearchQuery,
	});

	const handleSearch = (query: string) => {
		if (query.trim().length >= 2) {
			setActiveSearchQuery(query.trim());
		} else {
			setActiveSearchQuery("");
		}
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const userOrders =
		activeSearchQuery && activeSearchQuery.trim().length >= 2
			? searchData?.orders || []
			: ordersData?.orders || [];

	const filteredOrders = userOrders;

	if (!session?.user?.id) {
		return (
			<Layout>
				<div className="p-4 sm:p-8 min-h-[calc(100vh-4rem)] flex items-center justify-center ">
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.2 }}
						className="max-w-md w-full mx-auto"
					>
						<Card className="p-8 text-center border-dashed">
							<div className="flex flex-col items-center gap-4">
								<div className="p-3 bg-primary/10 rounded-full">
									<Icon name="LogIn" className="w-8 h-8 text-primary" />
								</div>
								<div>
									<h2 className="text-xl font-semibold mb-2">
										{t("orders.sign_in_required.header")}
									</h2>
									<p className="text-muted-foreground text-sm mb-6">
										{t("orders.sign_in_required.subheader")}
									</p>
									<Button asChild>
										<Link to="/auth/signin">
											{t("orders.sign_in_required.button")}
										</Link>
									</Button>
								</div>
							</div>
						</Card>
					</motion.div>
				</div>
			</Layout>
		);
	}

	// Loading state
	if (
		isLoading ||
		(activeSearchQuery && activeSearchQuery.trim().length >= 2 && isSearching)
	) {
		return (
			<Layout>
				<div className="container max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
					{/* Header Skeleton */}
					<div className="space-y-2">
						<Skeleton className="h-8 w-32" />
						<Skeleton className="h-4 w-48" />
					</div>

					{/* Search Skeleton */}
					<div>
						<Skeleton className="h-10 w-full sm:max-w-xs rounded-lg" />
					</div>

					{/* Orders List Skeleton */}
					<div className="space-y-3">
						{Array.from({ length: 5 }).map((_, index) => (
							<Card key={`skeleton-${index}`} className="p-4">
								<div className="flex items-center justify-between gap-4">
									<div className="flex items-center gap-4 flex-1">
										<Skeleton className="w-8 h-8 rounded" />
										<div className="space-y-1.5 flex-1">
											<Skeleton className="h-4 w-32" />
											<Skeleton className="h-3 w-24" />
										</div>
									</div>
									<Skeleton className="h-4 w-16" />
								</div>
							</Card>
						))}
					</div>
				</div>
			</Layout>
		);
	}

	// Error state
	if (error) {
		return (
			<Layout>
				<div className="container max-w-4xl mx-auto p-4 sm:p-6 flex items-center justify-center min-h-[50vh]">
					<Card className="p-8 text-center max-w-md w-full">
						<div className="flex flex-col items-center gap-4">
							<div className="p-3 bg-destructive/10 rounded-full">
								<Icon name="CircleAlert" className="w-8 h-8 text-destructive" />
							</div>
							<div>
								<h2 className="text-lg font-semibold mb-2">
									{t("orders.error.header")}
								</h2>
								<p className="text-sm text-muted-foreground mb-4">
									{t("orders.error.subheader")}
								</p>
								<Button onClick={() => refetch()} variant="outline" size="sm">
									{t("orders.error.retry")}
								</Button>
							</div>
						</div>
					</Card>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="p-4 md:p-8 border rounded-large min-h-full bg-gradient-main">
				<div className="container max-w-4xl mx-auto sm:p-6 min-h-full">
					{/* Header */}
					<div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
						<motion.div
							initial={{ opacity: 0, y: -10 }}
							animate={{ opacity: 1, y: 0 }}
							className="space-y-1"
						>
							<h1 className="text-2xl font-bold tracking-tight">
								{t("orders.header")}
							</h1>
							<p className="text-sm text-muted-foreground">
								{t("orders.subheader")}
							</p>
						</motion.div>

						<motion.div
							initial={{ opacity: 0, x: 10 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ delay: 0.1 }}
							className="w-full sm:w-auto"
						>
							<SearchInput
								placeholder={t("orders.search_placeholder")}
								debounceMs={300}
								onSubmit={handleSearch}
								className="w-full sm:w-[260px] h-9 text-sm"
							/>
						</motion.div>
					</div>

					{/* Active Search Indicator */}
					{activeSearchQuery && (
						<motion.div
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							className="mb-4 text-sm text-muted-foreground"
						>
							{t("orders.showing_results")}{" "}
							<span className="font-medium text-foreground">
								"{activeSearchQuery}"
							</span>
							<Button
								variant="link"
								className="h-auto p-0 ml-2 text-xs"
								onClick={() => {
									setActiveSearchQuery("");
								}}
							>
								{t("orders.clear")}
							</Button>
						</motion.div>
					)}

					{/* Orders List */}
					<div className="space-y-3">
						{filteredOrders.length === 0 ? (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.3 }}
							>
								<Card className="p-12 text-center border-dashed">
									<div className="flex flex-col items-center gap-4">
										<div className="p-4 bg-muted rounded-full">
											<Icon
												name="Package"
												className="w-8 h-8 text-muted-foreground"
											/>
										</div>
										<div className="max-w-xs mx-auto">
											<h3 className="text-lg font-semibold mb-1">
												{t("orders.empty.header")}
											</h3>
											<p className="text-sm text-muted-foreground mb-6">
												{activeSearchQuery &&
												activeSearchQuery.trim().length >= 2
													? t("orders.empty.no_search_results", {
															query: activeSearchQuery,
														})
													: t("orders.empty.no_orders")}
											</p>
											<Button asChild className="w-full sm:w-auto">
												<Link to="/">{t("orders.empty.start_ordering")}</Link>
											</Button>
										</div>
									</div>
								</Card>
							</motion.div>
						) : (
							(filteredOrders as any[]).map((order: any, index: number) => (
								<OrderCard
									key={order.id}
									order={order}
									formatDate={formatDate}
									index={index}
								/>
							))
						)}
					</div>
				</div>
			</div>
		</Layout>
	);
}
